import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex, Link } from "theme-ui";
import moment from "moment-timezone";

import { themevals } from "theme/themevals";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import { useToast } from "components/toast";
import { BackArrowIcon } from "components/svg";
import { Pill } from "containers/Leads/List";

import { getRfqDuplicates, mergeRfqDuplicates, dismissRfqDuplicates, reopenRfqDuplicates, type RfqDuplicateGroup, type RfqDuplicateMember } from "api/QuoteRequests";

/**
 * RFQ's > Duplicates (SESSION_STATE 2026-10-07, item 0.6), modelled on
 * Leads > Duplicates. One card per set of OPEN, UNQUOTED RFQs from the same
 * org, at the same site, for the same products. Merge keeps one (newest by
 * default), cancels the rest with a note and moves their notes across.
 * Quoted, accepted or project-linked RFQs never appear here.
 */

const STATUS: Record<number, string> = { 0: "Estimated", 1: "New" };
const label = (m: RfqDuplicateMember) => (m.sequence != null ? `#${m.sequence}` : "RFQ");

const RfqDuplicates: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [groups, setGroups] = useState<RfqDuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Per set: which RFQs stay open. Default = the newest only. More than one
  // can stay (e.g. separate play areas); the rest are canceled.
  const [keepIds, setKeepIds] = useState<Record<string, string[]>>({});
  const [merge, setMerge] = useState<{ group: RfqDuplicateGroup; survivor: RfqDuplicateMember; kept: RfqDuplicateMember[]; others: RfqDuplicateMember[] } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  // Sets already reviewed (kept or "not duplicates") that still have 2+
  // open RFQs - so a decision can be revisited (2026-10-08, Jack).
  const [reviewed, setReviewed] = useState<RfqDuplicateGroup[]>([]);
  const [showReviewed, setShowReviewed] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    const res: any = await getRfqDuplicates();
    setLoading(false);
    if (res?.error) return setError(res.error);
    setError(null);
    setGroups(res.groups ?? []);
    setReviewed(res.reviewed ?? []);
  }, []);

  const reopen = async (g: RfqDuplicateGroup) => {
    setBusyKey(g.key);
    const res: any = await reopenRfqDuplicates(g.members.map((m) => m.id));
    setBusyKey(null);
    if (res?.error) return setError(res.error);
    await refetch();
  };

  useEffect(() => {
    document.title = `${process.env.REACT_APP_NAME} | Duplicate RFQ's`;
    refetch();
  }, [refetch]);

  const keptOf = (g: RfqDuplicateGroup) => {
    const ids = keepIds[g.key] ?? [g.members[0].id];
    return g.members.filter((m) => ids.includes(m.id));
  };
  const toggleKeep = (g: RfqDuplicateGroup, id: string) =>
    setKeepIds((k) => {
      const cur = k[g.key] ?? [g.members[0].id];
      const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
      return { ...k, [g.key]: next.length ? next : cur }; // at least one stays open
    });
  const identical = groups.filter((g) => g.sameQuantity);

  const dismiss = async (g: RfqDuplicateGroup) => {
    setBusyKey(g.key);
    const res: any = await dismissRfqDuplicates(g.members.map((m) => m.id));
    setBusyKey(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add("Noted - this set will not be flagged again.", "var(--theme-ui-colors-success)");
    refetch();
  };

  const runMerge = async () => {
    if (!merge) return;
    setBusyKey(merge.group.key);
    const res: any = await mergeRfqDuplicates(merge.survivor.id, merge.others.map((m) => m.id));
    // Several kept: remember them as "not duplicates" of each other so the
    // set does not come back with just the kept ones in it.
    if (!res?.error && merge.kept.length > 1) await dismissRfqDuplicates(merge.kept.map((m) => m.id));
    setBusyKey(null);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    toast.add(`Kept ${merge.kept.map(label).join(", ")}; canceled ${res.merged.length}.`, "var(--theme-ui-colors-success)");
    setMerge(null);
    refetch();
  };

  const runBulk = async () => {
    setBusyKey("bulk");
    let ok = 0;
    let failed = 0;
    for (const g of identical) {
      const res: any = await mergeRfqDuplicates(g.members[0].id, g.members.slice(1).map((m) => m.id));
      if (res?.error) failed++;
      else ok++;
    }
    setBusyKey(null);
    setBulkOpen(false);
    toast.add(`Merged ${ok} set${ok === 1 ? "" : "s"}${failed ? `, ${failed} failed (no longer open)` : ""}.`, failed ? "var(--theme-ui-colors-danger)" : "var(--theme-ui-colors-success)");
    refetch();
  };

  return (
    <>
      <Header />
      <Page
        title="Duplicate RFQ's"
        subtitle="Open, unquoted RFQ's from the same organization, at the same site, for the same products. Keep one and cancel the rest, or mark them as not duplicates."
        backAction={<Link variant="back" href="/quote-requests"><BackArrowIcon />{"Back to RFQ's"}</Link>}
        titleAction={
          <Flex sx={{ gap: "8px" }}>
            {identical.length > 0 && (
              <BasicButton type="button" keepText onClick={() => setBulkOpen(true)} disabled={loading || !!busyKey}>
                Merge {identical.length} identical set{identical.length === 1 ? "" : "s"}
              </BasicButton>
            )}
            {reviewed.length > 0 && (
              <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => setShowReviewed((v) => !v)}>
                {showReviewed ? "Hide reviewed" : `Reviewed (${reviewed.length})`}
              </BasicButton>
            )}
            <BasicButton scheme="secondaryOutline" type="button" keepText onClick={refetch} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </BasicButton>
          </Flex>
        }
      >
        {error && (
          <Box sx={{ marginBottom: "16px", padding: "10px 14px", background: "#FBEAEA", color: "#A60000", borderRadius: "8px", fontFamily: "Poppins", fontSize: "13px" }}>{error}</Box>
        )}

        <P fontFamily="Poppins" fontSize={"13px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"16px"}>
          {loading
            ? "Checking…"
            : groups.length === 0
              ? "No duplicate RFQ's. Every open RFQ is for a different organization, site or product."
              : `${groups.length} set${groups.length === 1 ? "" : "s"}, ${groups.reduce((n, g) => n + g.members.length - 1, 0)} extra RFQ's. The newest stays open by default; use Keep this one to keep more than one. Sets with different quantities may be separate play areas at one address - check before merging.`}
        </P>

        {groups.map((g) => {
          const kept = keptOf(g);
          const survivor = kept[0];
          const others = g.members.filter((m) => !kept.some((k) => k.id === m.id));
          return (
            <Box key={g.key} sx={{ border: "1px solid #E4E7EB", borderRadius: "8px", padding: "16px", marginBottom: "16px", background: "#fff", fontFamily: "Poppins" }}>
              <Flex sx={{ justifyContent: "space-between", alignItems: "flex-start", gap: "12px", marginBottom: "8px", flexWrap: "wrap" }}>
                <Box>
                  <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>
                    {g.orgName} &middot; {g.siteName ?? "Unknown site"}{g.siteCity ? `, ${g.siteCity}` : ""}
                  </Box>
                  <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
                    {g.members.length} open {"RFQ's"} &middot; {g.products ?? "no product"}
                  </Box>
                </Box>
                <Flex sx={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                  {g.sameQuantity
                    ? <Pill bg="#FBEAEA" fg="#A60000">Same quantity</Pill>
                    : <Pill bg="#FAEEDA" fg="#633806" title="Could be separate play areas at one address">Quantities differ</Pill>}
                  <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => dismiss(g)} disabled={!!busyKey}>Not duplicates</BasicButton>
                  <BasicButton type="button" keepText onClick={() => setMerge({ group: g, survivor, kept, others })} disabled={!!busyKey || others.length === 0}>
                    {kept.length === 1 ? `Keep ${label(survivor)}, cancel ${others.length}` : `Keep ${kept.length}, cancel ${others.length}`}
                  </BasicButton>
                </Flex>
              </Flex>

              {g.members.map((m) => {
                const isKept = kept.some((k) => k.id === m.id);
                return (
                  <Flex key={m.id} sx={{ alignItems: "center", gap: "12px", padding: "10px 0", borderTop: "1px solid #F3F4F6" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#111", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ cursor: "pointer" }} onClick={() => navigate(`/quote-requests/${m.id}`)}>{label(m)}</span>
                        <span>{m.quantity != null ? `${m.quantity} yds` : "no quantity"}</span>
                        <Pill bg="#F1EFE8" fg="#5f5e5a">{STATUS[m.status] ?? m.status}</Pill>
                        {isKept
                          ? <Pill bg="#DCFCE7" fg="#166534">Stays open</Pill>
                          : <Pill bg="#FBEAEA" fg="#A60000">Will be canceled</Pill>}
                      </Box>
                      <Box sx={{ fontSize: "11px", color: "#78716C", marginTop: "4px" }}>
                        {[
                          m.requester ? `by ${m.requester}` : null,
                          `created ${moment(m.createdAt).format("MMM D, YYYY h:mm a")}`,
                          m.noteCount ? `${m.noteCount} note${m.noteCount === 1 ? "" : "s"}` : null,
                          m.projectName,
                        ].filter(Boolean).join(" · ")}
                      </Box>
                    </Box>
                    <Flex sx={{ gap: "6px", flexShrink: 0 }}>
                      <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => toggleKeep(g, m.id)} disabled={isKept && kept.length === 1}>
                        {isKept ? "Cancel this one" : "Keep this one"}
                      </BasicButton>
                      <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => navigate(`/quote-requests/${m.id}`)}>Open</BasicButton>
                    </Flex>
                  </Flex>
                );
              })}
            </Box>
          );
        })}

        {showReviewed && reviewed.length > 0 && (
          <Box sx={{ marginTop: "8px" }}>
            <P fontFamily="Poppins" fontSize={"13px"} fontWeight={600} marginTop={0} marginBottom={"4px"}>Reviewed</P>
            <P fontFamily="Poppins" fontSize={"12px"} color={themevals.colors.dkgrey} marginTop={0} marginBottom={"12px"}>
              {"Sets you kept or marked as not duplicates that still have 2+ open RFQ's. Review again puts a set back on the list above. A new matching RFQ brings a set back on its own."}
            </P>
            {reviewed.map((g) => (
              <Box key={g.key} sx={{ border: "1px solid #E4E7EB", borderRadius: "8px", padding: "12px 16px", marginBottom: "10px", background: "#FAFAF9", fontFamily: "Poppins" }}>
                <Flex sx={{ justifyContent: "space-between", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <Box>
                    <Box sx={{ fontSize: "13px", fontWeight: 600, color: "#111" }}>
                      {g.orgName} &middot; {g.siteName ?? "Unknown site"}{g.siteCity ? `, ${g.siteCity}` : ""}
                    </Box>
                    <Box sx={{ fontSize: "12px", color: themevals.colors.dkgrey, marginTop: "2px" }}>
                      {g.members.map((m) => `${label(m)}${m.quantity != null ? ` (${m.quantity} yds)` : ""}`).join(" · ")} &middot; {g.products ?? "no product"}
                    </Box>
                  </Box>
                  <BasicButton scheme="secondaryOutline" type="button" keepText onClick={() => reopen(g)} disabled={!!busyKey}>
                    {busyKey === g.key ? "Saving…" : "Review again"}
                  </BasicButton>
                </Flex>
              </Box>
            ))}
          </Box>
        )}

        {merge && (
          <BasicModal
            title={merge.kept.length === 1 ? `Keep ${label(merge.survivor)}?` : `Keep ${merge.kept.length} RFQ's?`}
            cancel_click={() => setMerge(null)}
            cancel_text="Cancel"
            confirm_text={busyKey === merge.group.key ? "Merging…" : `Cancel ${merge.others.length} RFQ${merge.others.length === 1 ? "" : "'s"}`}
            submitting={busyKey === merge.group.key}
            confirm_click={runMerge}
          >
            <P fontFamily="Poppins" fontSize={"13px"} marginTop={0} marginBottom={"8px"} color={themevals.colors.black}>
              {merge.others.map(label).join(", ")} will be canceled. <b>{merge.kept.map(label).join(", ")}</b> {merge.kept.length === 1 ? "stays" : "stay"} open.
            </P>
            <P fontFamily="Poppins" fontSize={"12px"} marginTop={0} marginBottom={0} color={themevals.colors.dkgrey}>
              Their notes move to {label(merge.survivor)}, and each gets a note saying it was a duplicate. Nothing is deleted.{merge.kept.length > 1 ? " The ones you keep are marked as not duplicates of each other." : ""}
            </P>
          </BasicModal>
        )}

        {bulkOpen && (
          <BasicModal
            title={`Merge ${identical.length} identical set${identical.length === 1 ? "" : "s"}?`}
            cancel_click={() => setBulkOpen(false)}
            cancel_text="Cancel"
            confirm_text={busyKey === "bulk" ? "Merging…" : "Merge"}
            submitting={busyKey === "bulk"}
            confirm_click={runBulk}
          >
            <P fontFamily="Poppins" fontSize={"13px"} marginTop={0} marginBottom={0} color={themevals.colors.black}>
              Only sets where every RFQ has the same quantity. In each, the newest is kept and the rest are canceled with a note. Sets with different quantities are left for you to check.
            </P>
          </BasicModal>
        )}
      </Page>
    </>
  );
};

export default RfqDuplicates;
